import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ShareableLinkContext from './ShareableLink.context';
import api from '../../api';
import { useParams } from 'react-router-dom';
import useQueryParams from 'hooks/useQueryParams';
import credentialsService from 'services/credentialsService';
import { ShareableLinkMainInfo } from 'models/ShareableLink';
import FourDotLoader from 'components/FourDotLoader';
import ShareableLinkNotFound from '../../components/ShareableLinkNotFound';
import ShareableLinkHasExpired from '../../components/ShareableLinkHasExpired';
import ShareableLinkUserForm from '../../components/ShareableLinkUserForm';
import ShareableLinkRequestAccess from '../../components/ShareableLinkRequestAccess';
import ShareableLinkPassword from '../../components/ShareableLinkPassword';
import shareableLinkSocketService from '../../services/socketService/shareableLinkSocketService';
import { AuthEventTypes } from 'modules/bus';
import downloadService from 'services/downloadService';
import uniqid from 'uniqid';
import ShareableLinkNdaConnecting from '../../components/ShareableLinkNdaConnecting';
import ShareableLinkNdaCountersign from '../../components/ShareableLinkNdaCountersign';
import ShareableLinkNdaSign from '../../components/ShareableLinkNdaSign';
import ShareableLinkNdaRejected from '../../components/ShareableLinkNdaRejected';
import useSocketEvent from '../../services/socketService/hooks/useSocketEvent';
import { SHAREABLE_LINK_LISTEN_EVENTS } from '../../services/socketService/socketEventNames';
import useRefetch from 'hooks/useRefetch';
import { NDAStatusUpdatedEventData } from '../../services/socketService/socketEventData';
import {
  getIsMainInfoNdaStatusReadyForSigning,
  getIsMainInfoNdaStatusRejected,
  getIsMainInfoNdaStatusWaitingConnection,
  getIsMainInfoNdaStatusWaitingCountersign,
} from '../../utils';
import useBusEvent from 'hooks/useBusEvent';
import useDocusignRedirect from 'hooks/useDocusignRedirect';
import ShareableLinkTermsAndConditions from '../../components/ShareableLinkTermsAndConditions';
import ShareableLinkWaitingRoom from '../../components/ShareableLinkWaitingRoom';
import ShareableLinkRejectedFromWaitingRoom from '../../components/ShareableLinkRejectedFromWaitingRoom';

const ShareableLinkProvider: React.FC = (props) => {
  const { children } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [mainInfo, setMainInfo] = useState<ShareableLinkMainInfo>();
  const [isPasswordConfirmed, setIsPasswordConfirmed] = useState(false);

  const isLoggedIn = useMemo(() => {
    const { hasAuthorized, hasPassword } = mainInfo || {};

    if (!hasAuthorized) return false;

    if (!hasPassword) return true;

    return isPasswordConfirmed;
  }, [isPasswordConfirmed, mainInfo]);

  const { shareableLinkId } = useParams<{ shareableLinkId: string }>();
  const {
    params: { emailToken },
  } = useQueryParams();

  const { isWaitingDocusingWebhook, removeDocusignQueryParams } =
    useDocusignRedirect();

  const { refetchFlag, refetch } = useRefetch();

  const updateAuthInfo = useCallback(
    (newMainInfo: ShareableLinkMainInfo, newIsPasswordConfirmed?: boolean) => {
      if (newMainInfo?.authorizationToken) {
        credentialsService.shareableLinkToken = newMainInfo.authorizationToken;
      }

      if (newIsPasswordConfirmed) {
        setIsPasswordConfirmed(newIsPasswordConfirmed);
      }

      setMainInfo((prev) => ({ ...prev, ...newMainInfo }));
    },
    [],
  );

  const ndaStatusUpdated = useCallback(
    ({
      shareableLinkId: eventDataShareableLinkId,
      email: eventDataEmail,
    }: NDAStatusUpdatedEventData) => {
      if (
        shareableLinkId === eventDataShareableLinkId &&
        mainInfo?.email === eventDataEmail
      ) {
        removeDocusignQueryParams();
        refetch();
      }
    },
    [shareableLinkId, mainInfo?.email, removeDocusignQueryParams, refetch],
  );

  useEffect(() => {
    if (isWaitingDocusingWebhook) {
      setIsPasswordConfirmed(true);
    }
  }, [isWaitingDocusingWebhook]);

  useEffect(() => {
    if (isLoggedIn) {
      shareableLinkSocketService.connect();

      downloadService.secureLinkDownloadUniqueId = uniqid();

      return () => {
        shareableLinkSocketService.disconnect();
        downloadService.removeSecureLinkDownloadUniqueId();
      };
    }
  }, [isLoggedIn]);

  const logout = useCallback(() => {
    credentialsService.removeShareableLinkToken();
    setMainInfo((prev) => ({
      ...prev,
      authorizationToken: '',
      hasAuthorized: false,
    }));
    setIsPasswordConfirmed(false);
  }, []);

  useBusEvent(AuthEventTypes.SL_LOGOUT_SIGNAL, logout);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        const { data } = await api.shareableLinks.getShareableLinkMainInfo(
          shareableLinkId,
          credentialsService.shareableLinkToken,
          emailToken,
        );

        setMainInfo(data);
      } catch {
        return;
      } finally {
        setIsLoading(false);
      }
    })();
  }, [shareableLinkId, emailToken, refetchFlag]);

  useSocketEvent(
    SHAREABLE_LINK_LISTEN_EVENTS.NDA_STATUS_UPDATED,
    ndaStatusUpdated,
  );

  if (isLoading) return <FourDotLoader />;

  if (mainInfo?.doesNotExist) return <ShareableLinkNotFound />;

  if (mainInfo?.hasExpired)
    return (
      <ShareableLinkHasExpired
        shareableLinkId={shareableLinkId}
        mainInfo={mainInfo}
      />
    );

  if (!mainInfo?.hasAuthorized) {
    return (
      <ShareableLinkUserForm
        shareableLinkId={shareableLinkId}
        mainInfo={mainInfo}
        updateAuthInfo={updateAuthInfo}
      />
    );
  }

  if (mainInfo?.doesNotHaveAccess)
    return (
      <ShareableLinkRequestAccess
        shareableLinkId={shareableLinkId}
        mainInfo={mainInfo}
      />
    );

  if (mainInfo?.hasPassword && !isPasswordConfirmed)
    return (
      <ShareableLinkPassword
        shareableLinkId={shareableLinkId}
        mainInfo={mainInfo}
        updateAuthInfo={updateAuthInfo}
      />
    );

  if (mainInfo?.hasNDA) {
    if (getIsMainInfoNdaStatusReadyForSigning(mainInfo?.ndaStatus))
      return <ShareableLinkNdaSign isProcessing={isWaitingDocusingWebhook} />;

    if (getIsMainInfoNdaStatusWaitingConnection(mainInfo?.ndaStatus))
      return <ShareableLinkNdaConnecting />;

    if (getIsMainInfoNdaStatusWaitingCountersign(mainInfo?.ndaStatus))
      return <ShareableLinkNdaCountersign />;

    if (getIsMainInfoNdaStatusRejected(mainInfo?.ndaStatus))
      return <ShareableLinkNdaRejected />;
  }

  if (mainInfo?.hasTermsAndConditions) {
    return (
      <ShareableLinkTermsAndConditions
        shareableLinkId={shareableLinkId}
        termsAndConditionsContent={mainInfo?.termsAndConditionsContent}
        updateAuthInfo={updateAuthInfo}
      />
    );
  }

  if (mainInfo?.addedToWaitingRoom) {
    return (
      <ShareableLinkWaitingRoom
        isEmailNotificationTurnOn={mainInfo?.isEmailNotificationTurnOn}
        isPlatformNotificationTurnOn={mainInfo?.isPlatformNotificationTurnOn}
        isSMSNotificationTurnOn={mainInfo?.isSMSNotificationTurnOn}
      />
    );
  }

  if (mainInfo?.rejectedFromWaitingRoom) {
    return <ShareableLinkRejectedFromWaitingRoom />;
  }

  return (
    <ShareableLinkContext.Provider value={{ isLoggedIn, mainInfo }}>
      {children}
    </ShareableLinkContext.Provider>
  );
};

export default ShareableLinkProvider;
